import { useTranslations } from "use-intl";
import { useCallback, useMemo, useState } from "react";
import { useSuspenseQuery } from "@apollo/client";
import { useSearchCompanyDocumentParam } from "@/modules/documents/features/search-company-document-in-tree";
import { GET_COMPANY_DOCUMENT_TREE } from "../api/get-company-document-tree";
import { treeNodesToDataNodes } from "../utils/tree-nodes-to-data-nodes";
import { isFolderNode } from "../utils/tree-node-guards";
import { CompanyDocumentTreeNode } from "./types";

export function useCompanyDocumentTree() {
  const { search } = useSearchCompanyDocumentParam();
  const tc = useTranslations("Common");
  const { data } = useSuspenseQuery(GET_COMPANY_DOCUMENT_TREE, {
    variables: { search },
  });
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [selectedKey, setSelectedKey] = useState<string>();

  const nodes = useMemo<CompanyDocumentTreeNode[]>(
    () => data.getCompanyDocumentTree,
    [data.getCompanyDocumentTree],
  );

  const treeData = useMemo(
    () => treeNodesToDataNodes(nodes, tc("Hidden")),
    [nodes, tc],
  );

  const nodeById = useMemo(
    () => new Map(nodes.map((node) => [node.id, node])),
    [nodes],
  );

  const folderKeys = useMemo(
    () => nodes.filter(isFolderNode).map((node) => node.id),
    [nodes],
  );

  const selectedNode = useMemo(
    () => nodes.find((node) => node.id === selectedKey),
    [nodes, selectedKey],
  );

  const searching = !!search.trim();

  const expand = useCallback(
    (key: string) =>
      setExpandedKeys((keys) => (keys.includes(key) ? keys : [...keys, key])),
    [],
  );

  return {
    treeData,
    nodes,
    nodeById,
    searching,
    expandedKeys: searching ? folderKeys : expandedKeys,
    setExpandedKeys,
    expand,
    selectedKey,
    selectedNode,
    select: setSelectedKey,
  };
}
